import type { WizardFormData } from '../../../../types/wizard';
import { useTranslation } from 'react-i18next';
import StepCard from '../../../../components/wizard/StepCard';

interface Step5TimeProps {
  formData: WizardFormData;
  updateFormData: (data: Partial<WizardFormData>) => void;
  errors: Record<string, string>;
}

const Step5Time = ({ formData, updateFormData, errors }: Step5TimeProps) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.resolvedLanguage?.startsWith('en');
  const timePreferences = [
    {
      value: 'manha',
      label: isEnglish ? 'Morning' : 'Manhã',
      description: isEnglish ? 'I prefer starting the day early' : 'Prefiro começar o dia cedo',
      icon: 'ri-sun-line',
    },
    {
      value: 'tarde',
      label: isEnglish ? 'Afternoon' : 'Tarde',
      description: isEnglish ? 'I like waking up later' : 'Gosto de acordar mais tarde',
      icon: 'ri-cloudy-line',
    },
    {
      value: 'noite',
      label: isEnglish ? 'Night' : 'Noite',
      description: isEnglish ? 'I am more active at night' : 'Sou mais ativo à noite',
      icon: 'ri-moon-line',
    },
    {
      value: 'flexivel',
      label: isEnglish ? 'Flexible' : 'Flexível',
      description: isEnglish ? 'I adapt easily' : 'Me adapto facilmente',
      icon: 'ri-time-line',
    },
  ];

  return (
    <div className="space-y-8">
      <StepCard>
        <div className="mb-6">
          <label className="block text-white text-lg font-medium mb-2">
            {isEnglish ? 'When do you prefer doing activities?' : 'Quando você prefere fazer atividades?'} <span className="text-[#FFD700]">*</span>
          </label>
          <p className="text-white/90 text-sm mb-4">
            {isEnglish
              ? 'This helps us organize the best timing for each experience'
              : 'Isso nos ajuda a organizar o melhor horário para cada experiência'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {timePreferences.map((time) => (
            <button
              key={time.value}
              type="button"
              onClick={() =>
                updateFormData({
                  step5_time: {
                    ...formData.step5_time,
                    preferredTime: time.value as any,
                  },
                })
              }
              className={`p-5 rounded-xl border-2 transition-all duration-200 text-left ${
                formData.step5_time.preferredTime === time.value
                  ? 'border-[#FFD700] bg-[#FFD700]/10'
                  : 'border-white/10 bg-white/5 hover:border-white/20'
              }`}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    formData.step5_time.preferredTime === time.value
                      ? 'bg-[#C9A84C]/20 border border-[#C9A84C]/30 text-[#E8D4A0]'
                      : 'bg-white/10 text-white'
                  }`}
                >
                  <i className={`${time.icon} text-2xl`}></i>
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium text-base mb-1">{time.label}</p>
                  <p className="text-white/90 text-sm">{time.description}</p>
                </div>
                {formData.step5_time.preferredTime === time.value && (
                  <i className="ri-checkbox-circle-fill text-[#E8D4A0] text-xl flex-shrink-0"></i>
                )}
              </div>
            </button>
          ))}
        </div>

        {(errors.preferredTime || errors.timePreference) && (
          <p className="text-red-400 text-sm mt-4 flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {errors.preferredTime || errors.timePreference}
          </p>
        )}
      </StepCard>

      {/* Preferências Adicionais */}
      <StepCard>
        <div className="mb-4">
          <label className="block text-white text-base font-medium mb-4">
            {isEnglish ? 'Additional preferences (optional)' : 'Preferências adicionais (opcional)'}
          </label>
        </div>

        <div className="space-y-3">
          <label className="flex items-center gap-3 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-all cursor-pointer">
            <input
              type="checkbox"
              checked={formData.step5_time.earlyRiser}
              onChange={(e) =>
                updateFormData({
                  step5_time: {
                    ...formData.step5_time,
                    earlyRiser: e.target.checked,
                  },
                })
              }
              className="w-5 h-5 rounded border-white/20 bg-white/10 text-[#FFD700] focus:ring-[#FFD700] focus:ring-offset-0"
            />
            <div className="flex-1">
              <p className="text-white font-medium text-sm">{isEnglish ? 'I naturally wake up early' : 'Acordo cedo naturalmente'}</p>
              <p className="text-white/90 text-xs mt-1">
                {isEnglish
                  ? 'I enjoy sunrise moments and calm mornings'
                  : 'Gosto de aproveitar o nascer do sol e manhãs tranquilas'}
              </p>
            </div>
          </label>

          <label className="flex items-center gap-3 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-all cursor-pointer">
            <input
              type="checkbox"
              checked={formData.step5_time.nightOwl}
              onChange={(e) =>
                updateFormData({
                  step5_time: {
                    ...formData.step5_time,
                    nightOwl: e.target.checked,
                  },
                })
              }
              className="w-5 h-5 rounded border-white/20 bg-white/10 text-[#FFD700] focus:ring-[#FFD700] focus:ring-offset-0"
            />
            <div className="flex-1">
              <p className="text-white font-medium text-sm">{isEnglish ? 'I enjoy nightlife' : 'Gosto de vida noturna'}</p>
              <p className="text-white/90 text-xs mt-1">
                {isEnglish
                  ? 'I prefer evening experiences, bars and restaurant-driven plans'
                  : 'Prefiro experiências noturnas, bares e restaurantes à noite'}
              </p>
            </div>
          </label>
        </div>
      </StepCard>
    </div>
  );
};

export default Step5Time;
